/**
 * Единый контракт ошибки API.
 * @typedef {Object} ApiError
 * @property {string} message - Текст ошибки для отображения
 * @property {number} status - HTTP-статус (0 при сетевой/отмене)
 * @property {*} [data] - Тело ответа (error.response.data)
 * @property {boolean} [canceled] - true если запрос отменён (AbortController / axios cancel)
 */

/**
 * Проверяет, что объект уже в формате ApiError (имеет message и status).
 * @param {*} obj
 * @returns {boolean}
 */
export function isApiError(obj) {
  return (
    obj != null &&
    typeof obj === 'object' &&
    typeof obj.message === 'string' &&
    typeof obj.status === 'number'
  );
}

/**
 * Нормализует любую ошибку (сырой axios error, Error, уже ApiError) в формат ApiError.
 * @param {*} error - объект из catch (axios reject, Error, или уже { message, status, data?, canceled? })
 * @returns {ApiError} - { message, status, data?, canceled? }
 */
export function normalizeApiError(error) {
  if (isApiError(error)) {
    return {
      message: error.message,
      status: error.status,
      ...(error.data !== undefined && { data: error.data }),
      ...(error.canceled === true && { canceled: true })
    };
  }

  // Отмена запроса (axios / AbortController)
  const isCanceled =
    error?.canceled === true ||
    error?.message === 'canceled' ||
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'AbortError' ||
    (typeof error?.message === 'string' && error.message.toLowerCase().includes('cancel'));

  if (isCanceled) {
    return { message: 'canceled', status: 0, canceled: true };
  }

  // Ответ сервера (axios: error.response)
  if (error?.response) {
    const data = error.response.data || {};
    const toMessage = (value) => {
      if (typeof value === 'string' && value.trim()) return value;
      if (Array.isArray(value)) {
        for (const item of value) {
          const itemMsg = toMessage(item);
          if (itemMsg) return itemMsg;
        }
      }
      if (value && typeof value === 'object') {
        if (typeof value.message === 'string' && value.message.trim()) return value.message;
        if (typeof value.msg === 'string' && value.msg.trim()) return value.msg;
        if (typeof value.error === 'string' && value.error.trim()) return value.error;
        if (value.error && typeof value.error === 'object') {
          const nested = toMessage(value.error);
          if (nested) return nested;
        }
      }
      return '';
    };
    const msg =
      toMessage(data.detail) ||
      toMessage(data.message) ||
      toMessage(data.error) ||
      'Ошибка сервера';
    return {
      message: msg,
      status: error.response.status,
      data,
      canceled: false
    };
  }

  // Запрос отправлен, ответа нет (сеть)
  if (error?.request) {
    return {
      message: 'Сервер не отвечает. Пожалуйста, попробуйте позже.',
      status: 0,
      canceled: false
    };
  }

  // Прочие (Error или объект с message)
  const message =
    error?.message && typeof error.message === 'string'
      ? error.message
      : 'Ошибка при выполнении запроса';
  return { message, status: 0, canceled: false };
}
